/**
 * ImportPage — full-page bank statement importer with import history.
 * Replaces the buried modal trigger in VariableSpending.
 */
import React, { useState } from 'react';
import Import from './Import';
import { getAllCats } from '../utils';

const fc = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

export default function ImportPage({ state, updateState, showToast }) {
  const [importing, setImporting] = useState(false);
  const history = state.importHistory || [];
  const rules   = state.importRules  || [];
  const cats    = getAllCats(state.customCats || [], state.disabledCats || []);
  const catMap  = Object.fromEntries(cats.map((c) => [c.id, `${c.em} ${c.name}`]));
  const deleteRule = (id) => updateState({ importRules: rules.filter((r) => r.id !== id) });

  // Undo the most recent import (only within 24 h)
  const latestImport = history[0];
  const canUndo = latestImport?._undo && (Date.now() - (latestImport.ts || 0)) < 86400000;

  const handleUndo = () => {
    if (!canUndo) return;
    const { txKeys, incomeIds, billIds, subIds } = latestImport._undo;

    // Remove transactions
    const newVarSpend = JSON.parse(JSON.stringify(state.varSpend || {}));
    txKeys.forEach(({ moKey, catId, ts, amt, place }) => {
      const entries = newVarSpend[moKey]?.[catId] || [];
      const idx = entries.findIndex((e) => Math.abs(e.amt - amt) < 0.01 && e.place === place && (ts === null || Math.abs((e.ts || 0) - ts) < 86400000));
      if (idx !== -1) entries.splice(idx, 1);
    });

    // Remove income entries
    const newIncomeLog = (state.incomeLog || []).filter((e) => !incomeIds.includes(e.id));

    // Remove added bills and subs
    const newBills = (state.fixedBills || []).filter((b) => !billIds.includes(b.id));
    const newSubs  = (state.subscriptions || []).filter((s) => !subIds.includes(s.id));

    // Remove from history
    const newHistory = history.filter((h) => h.id !== latestImport.id);

    updateState({ varSpend: newVarSpend, incomeLog: newIncomeLog, fixedBills: newBills, subscriptions: newSubs, importHistory: newHistory });
    showToast('↩️ Import undone');
  };

  return (
    <div>
      {!importing ? (
        <>
          {/* Hero CTA */}
          <div style={{ background: 'linear-gradient(135deg, var(--surf2), var(--surf))', borderRadius: 16, padding: '28px 24px', marginBottom: 16, textAlign: 'center', border: '1px solid var(--bdr)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📥</div>
            <div style={{ fontSize: 20, fontWeight: 900, marginBottom: 6 }}>Import Bank Statement</div>
            <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' }}>
              Upload a CSV, paste text, or take a screenshot — AI will parse every transaction, classify spending, detect recurring bills, and log income automatically.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
              {[
                { em: '📄', label: 'CSV Export', desc: 'Most accurate' },
                { em: '📋', label: 'Paste Text', desc: 'Copy from browser' },
                { em: '📸', label: 'Screenshot', desc: 'Photo of statement' },
              ].map(m => (
                <div key={m.label} style={{ padding: '10px 16px', background: 'var(--surf)', borderRadius: 12, border: '1px solid var(--bdr)', minWidth: 100 }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{m.em}</div>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--grn)', fontWeight: 700 }}>{m.desc}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setImporting(true)}
              style={{ padding: '13px 36px', borderRadius: 12, background: 'var(--ylw)', color: '#000', fontWeight: 900, fontSize: 15, border: 'none', cursor: 'pointer' }}
            >
              Start Import →
            </button>
          </div>

          {/* Auto-categorize Rules */}
          {rules.length > 0 && (
            <div className="card cp" style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div className="st" style={{ marginBottom: 0 }}>📌 Auto-Categorize Rules</div>
                <span style={{ fontSize: 11, color: 'var(--mut)' }}>{rules.length} rule{rules.length !== 1 ? 's' : ''}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 10 }}>
                When importing, transactions matching these keywords will be auto-categorized.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rules.map((rule) => (
                  <div key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: 'var(--surf2)', borderRadius: 8, border: '1px solid var(--bdr)' }}>
                    <span style={{ flex: 1, fontSize: 12, fontWeight: 600 }}>"{rule.match}"</span>
                    <span style={{ fontSize: 11, color: 'var(--mut)' }}>→</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ylw)' }}>{catMap[rule.catId] || rule.catId}</span>
                    <button
                      onClick={() => deleteRule(rule.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                      title="Delete rule"
                    >✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Import History */}
          <div className="card cp">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="st" style={{ marginBottom: 0 }}>📋 Import History</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {canUndo && (
                  <button
                    onClick={handleUndo}
                    style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 7, border: '1.5px solid rgba(255,107,107,.5)', background: 'rgba(255,107,107,.08)', color: 'var(--red)', cursor: 'pointer' }}
                  >
                    ↩️ Undo last import
                  </button>
                )}
                {history.length > 0 && (
                  <span style={{ fontSize: 11, color: 'var(--mut)' }}>{history.length} import{history.length !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>

            {history.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px', color: 'var(--mut)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📭</div>
                <div style={{ fontSize: 13 }}>No imports yet — try your first one above!</div>
                <div style={{ fontSize: 11, marginTop: 6 }}>Every import is logged here so you can track what was added when.</div>
              </div>
            ) : (
              history.map((h, i) => (
                <div key={h.id} style={{ padding: '12px 0', borderBottom: i < history.length - 1 ? '1px solid var(--bdr)' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--surf2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                      📥
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                        {new Date(h.date + 'T12:00:00').toLocaleDateString('default', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 11, color: 'var(--mut)' }}>
                        {h.txCount > 0 && <span>📊 {h.txCount} transactions</span>}
                        {h.incomeCount > 0 && <span>💵 {h.incomeCount} income</span>}
                        {h.billsAdded > 0 && <span>📋 {h.billsAdded} bill{h.billsAdded !== 1 ? 's' : ''} added</span>}
                        {h.subsAdded > 0 && <span>📱 {h.subsAdded} sub{h.subsAdded !== 1 ? 's' : ''} added</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {h.totalSpend > 0 && (
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)' }}>−{fc(h.totalSpend)}</div>
                      )}
                      {h.totalIncome > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--grn)', fontWeight: 700 }}>+{fc(h.totalIncome)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <div>
          <button
            onClick={() => setImporting(false)}
            style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 13, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            ← Back to history
          </button>
          <Import
            inline
            state={state}
            updateState={updateState}
            showToast={showToast}
            onComplete={() => setImporting(false)}
          />
        </div>
      )}
    </div>
  );
}
